<template>
  <v-container fluid class="enhanced-dashboard pa-6">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-6">
          <v-icon large left color="primary">mdi-view-dashboard</v-icon>
          Admin Dashboard
        </h1>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-h6">Lade Dashboard-Daten...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-if="error && !loading">
      <v-col cols="12">
        <v-alert type="error" prominent>
          <v-row align="center">
            <v-col class="grow">{{ error }}</v-col>
            <v-col class="shrink">
              <v-btn @click="fetchData" color="white" outlined>Erneut versuchen</v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Dashboard Content -->
    <div v-if="!loading && !error && stats">
      <!-- Summary Card -->
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" elevation="4">
            <v-card-text>
              <div class="text-overline mb-1">Bestellungen Gesamt</div>
              <div class="text-h3 primary--text">{{ stats.totalOrders }}</div>
              <div class="text-caption grey--text">
                <v-icon small>mdi-clock-outline</v-icon>
                Zuletzt aktualisiert: {{ formatTimestamp(stats.timestamp) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" elevation="4">
            <v-card-text>
              <div class="text-overline mb-1">Heute gepackt</div>
              <div class="text-h3 green--text text--darken-1">{{ stats.packedToday || 0 }}</div>
              <div class="text-caption grey--text">
                <v-icon small>mdi-package-check</v-icon>
                Artikel heute fertiggestellt
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" elevation="4">
            <v-card-text>
              <div class="text-overline mb-1">Prime Bestellungen</div>
              <div class="text-h3 amber--text text--darken-2">{{ stats.primeOrders || 0 }}</div>
              <div class="text-caption grey--text">
                <v-icon small>mdi-lightning-bolt</v-icon>
                {{ getPrimePercentage() }}% der Gesamt
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="summary-card" elevation="4">
            <v-card-text>
              <div class="text-overline mb-1">Eindeutige SKUs</div>
              <div class="text-h3 info--text">{{ getTotalUniqueSKUs() }}</div>
              <div class="text-caption grey--text">
                <v-icon small>mdi-package-variant</v-icon>
                Über alle Shops
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Order Processing & Production Overview -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title>
              <v-icon left large color="indigo">mdi-clipboard-list</v-icon>
              <span class="text-h5">Auftragsverarbeitung & Produktion</span>
            </v-card-title>
            <v-card-text>
              <!-- Order Types Section -->
              <div class="mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  <v-icon small left color="purple">mdi-cart-variant</v-icon>
                  Auftragstypen
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card color="purple lighten-5" elevation="1" class="compact-card">
                      <v-card-text class="py-3">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="purple" class="mr-2">mdi-cart-multiple</v-icon>
                            <div>
                              <div class="text-caption text-uppercase">Multicart</div>
                              <div class="text-h5 purple--text">{{ stats.multicartOrders || 0 }}</div>
                            </div>
                          </div>
                          <div class="text-caption grey--text">
                            {{ getOrderTypePercentage(stats.multicartOrders) }}%
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card color="blue lighten-5" elevation="1" class="compact-card">
                      <v-card-text class="py-3">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="blue" class="mr-2">mdi-cart</v-icon>
                            <div>
                              <div class="text-caption text-uppercase">Singlecart</div>
                              <div class="text-h5 blue--text">{{ stats.singlecartOrders || 0 }}</div>
                            </div>
                          </div>
                          <div class="text-caption grey--text">
                            {{ getOrderTypePercentage(stats.singlecartOrders) }}%
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- Multicart Shelf Status -->
              <div class="mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  <v-icon small left color="green">mdi-shelf</v-icon>
                  Multicart Regal-Status
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card color="green lighten-5" elevation="1" class="compact-card">
                      <v-card-text class="py-3">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
                            <div>
                              <div class="text-caption text-uppercase">Packbereit</div>
                              <div class="text-h5 green--text">{{ stats.multicartReady || 0 }}</div>
                            </div>
                          </div>
                          <div class="text-caption grey--text">
                            {{ getMulticartReadyPercentage() }}% von Multicart
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card color="orange lighten-5" elevation="1" class="compact-card">
                      <v-card-text class="py-3">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="orange" class="mr-2">mdi-clock-alert</v-icon>
                            <div>
                              <div class="text-caption text-uppercase">Nicht bereit</div>
                              <div class="text-h5 orange--text">{{ stats.multicartNotReady || 0 }}</div>
                            </div>
                          </div>
                          <div class="text-caption grey--text">
                            {{ getMulticartNotReadyPercentage() }}% von Multicart
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- Production Files Status -->
              <div class="mb-2">
                <div class="text-subtitle-1 font-weight-bold mb-3">
                  <v-icon small left color="indigo">mdi-file-download</v-icon>
                  Produktionsdateien-Status
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card color="green lighten-5" elevation="1" class="compact-card">
                      <v-card-text class="py-3">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="green darken-2" class="mr-2">mdi-check-circle</v-icon>
                            <div>
                              <div class="text-caption text-uppercase">Produziert / Heruntergeladen</div>
                              <div class="text-h5 green--text text--darken-2">{{ stats.ordersInProduction || 0 }}</div>
                            </div>
                          </div>
                          <div class="text-caption grey--text">
                            {{ getProductionPercentage(stats.ordersInProduction) }}% der Gesamt
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card color="red lighten-5" elevation="1" class="compact-card">
                      <v-card-text class="py-3">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="red darken-1" class="mr-2">mdi-alert-circle</v-icon>
                            <div>
                              <div class="text-caption text-uppercase">Noch nicht produziert / heruntergeladen</div>
                              <div class="text-h5 red--text text--darken-1">{{ stats.ordersNotInProduction || 0 }}</div>
                            </div>
                          </div>
                          <div class="text-caption grey--text">
                            {{ getProductionPercentage(stats.ordersNotInProduction) }}% der Gesamt
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- Shop Breakdown -->
              <v-divider class="my-4"></v-divider>
              <div class="text-subtitle-1 font-weight-bold mb-3">
                <v-icon small left>mdi-store-outline</v-icon>
                Nach Shop
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Shop</th>
                      <th class="text-center">Multicart</th>
                      <th class="text-center">Singlecart</th>
                      <th class="text-center">Prime</th>
                      <th class="text-center">MC Bereit</th>
                      <th class="text-center">MC Nicht bereit</th>
                      <th class="text-center">Dateien bereit</th>
                      <th class="text-center">Dateien fehlen</th>
                      <th class="text-right">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(shopData, shopName) in stats.byShop" :key="shopName">
                      <td>
                        <v-chip small :color="getShopColor(shopName)" dark>
                          <v-icon small left>{{ getShopIcon(shopName) }}</v-icon>
                          {{ formatShopName(shopName) }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="purple" outlined>
                          <v-icon x-small left>mdi-cart-multiple</v-icon>
                          {{ shopData.multicartOrders || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="blue" outlined>
                          <v-icon x-small left>mdi-cart</v-icon>
                          {{ shopData.singlecartOrders || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="amber darken-2" outlined>
                          <v-icon x-small left>mdi-lightning-bolt</v-icon>
                          {{ shopData.primeOrders || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="green" outlined>
                          <v-icon x-small left>mdi-check-circle</v-icon>
                          {{ shopData.multicartReady || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="orange" outlined>
                          <v-icon x-small left>mdi-alert-circle</v-icon>
                          {{ shopData.multicartNotReady || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="green darken-2" outlined>
                          <v-icon x-small left>mdi-check-circle</v-icon>
                          {{ shopData.ordersInProduction || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small color="red darken-1" outlined>
                          <v-icon x-small left>mdi-alert-circle</v-icon>
                          {{ shopData.ordersNotInProduction || 0 }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <strong>{{ shopData.orderCount }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Old Orders Alert -->
              <div v-if="stats.oldOrders && stats.oldOrders.length > 0" class="mt-6">
                <v-divider class="mb-4"></v-divider>
                <v-alert type="warning" prominent border="left" class="mb-3">
                  <v-row align="center">
                    <v-col class="grow">
                      <div class="text-h6">
                        <v-icon left>mdi-alert-circle</v-icon>
                        Auftragsalter-Warnung
                      </div>
                      <div class="text-body-2">
                        {{ filteredOldOrders.length }} Bestellung(en) sind älter als 7 Tage und noch nicht versendet
                        <span v-if="selectedShopFilter.length > 0"> (gefiltert nach {{ selectedShopFilter.length }} Shop(s))</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-alert>

                <!-- Shop Filter -->
                <v-row class="mb-3">
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="selectedShopFilter"
                      :items="availableShopsInOldOrders"
                      label="Nach Shop filtern"
                      multiple
                      chips
                      clearable
                      dense
                      outlined
                      prepend-inner-icon="mdi-filter"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 3"
                          small
                          close
                          :color="getShopColor(item)"
                          dark
                          @click:close="removeShopFilter(item)"
                        >
                          <v-icon x-small left>{{ getShopIcon(item) }}</v-icon>
                          {{ formatShopName(item) }}
                        </v-chip>
                        <span
                          v-if="index === 3"
                          class="grey--text text-caption"
                        >
                          (+{{ selectedShopFilter.length - 3 }} weitere)
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-chip small :color="getShopColor(item)" dark>
                            <v-icon x-small left>{{ getShopIcon(item) }}</v-icon>
                            {{ formatShopName(item) }}
                          </v-chip>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="oldOrdersHeaders"
                  :items="filteredOldOrders"
                  :items-per-page="10"
                  :footer-props="{
                    'items-per-page-options': [10, 25, 50]
                  }"
                  class="old-orders-table clickable-rows"
                  dense
                  :sort-by="['daysOld']"
                  :sort-desc="[true]"
                  @click:row="openOrderInSupport"
                >
                  <template v-slot:[`item.orderId`]="{ item }">
                    <span class="font-weight-bold">{{ item.orderId }}</span>
                  </template>

                  <template v-slot:[`item.shop`]="{ item }">
                    <v-chip x-small :color="getShopColor(item.shop)" dark>
                      <v-icon x-small left>{{ getShopIcon(item.shop) }}</v-icon>
                      {{ formatShopName(item.shop) }}
                    </v-chip>
                  </template>

                  <template v-slot:[`item.acsCustomer`]="{ item }">
                    <span v-if="item.acsCustomer">{{ item.acsCustomer }}</span>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:[`item.daysOld`]="{ item }">
                    <v-chip
                      small
                      :color="getDaysOldColor(item.daysOld)"
                      dark
                    >
                      {{ item.daysOld }} Tage
                    </v-chip>
                  </template>

                  <template v-slot:[`item.isPrime`]="{ item }">
                    <v-icon v-if="item.isPrime" small color="amber darken-2">mdi-lightning-bolt</v-icon>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:[`item.customer`]="{ item }">
                    {{ item.customer.firstName }} {{ item.customer.lastName }}
                  </template>

                  <template v-slot:[`item.createDate`]="{ item }">
                    {{ formatDate(item.createDate) }}
                  </template>
                </v-data-table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Overall SKU Overview -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <v-icon left large color="primary">mdi-magnify-scan</v-icon>
              <span class="text-h5">SKU-Übersicht (Alle Shops)</span>
              <v-spacer></v-spacer>
              <v-chip color="primary" outlined>
                {{ getOverallSKUData().length }} eindeutige SKUs
              </v-chip>
            </v-card-title>

            <v-card-text>
              <!-- Search Bar -->
              <v-text-field
                v-model="skuSearch"
                prepend-inner-icon="mdi-magnify"
                label="SKUs suchen..."
                outlined
                dense
                clearable
                class="mb-4"
              ></v-text-field>

              <!-- SKU Data Table -->
              <v-data-table
                :headers="skuTableHeaders"
                :items="filteredSKUData"
                :items-per-page="10"
                :search="skuSearch"
                :footer-props="{
                  'items-per-page-options': [10, 25, 50, 100]
                }"
                class="sku-overview-table"
                dense
              >
                <template v-slot:[`item.sku`]="{ item }">
                  <span class="font-weight-bold">{{ item.sku }}</span>
                </template>

                <template v-slot:[`item.quantity`]="{ item }">
                  <v-chip small color="primary" outlined>
                    {{ item.quantity }}
                  </v-chip>
                </template>

                <template v-slot:[`item.percentage`]="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="item.percentage"
                      height="20"
                      :color="getProgressColor(item.percentage)"
                      class="mr-2"
                      style="max-width: 100px;"
                    >
                      <template v-slot:default>
                        <small class="white--text">{{ item.percentage.toFixed(1) }}%</small>
                      </template>
                    </v-progress-linear>
                  </div>
                </template>

                <template v-slot:[`item.shops`]="{ item }">
                  <v-chip-group>
                    <v-chip
                      v-for="shop in item.shops"
                      :key="shop"
                      x-small
                      :color="getShopColor(shop)"
                      dark
                    >
                      {{ formatShopName(shop) }}
                    </v-chip>
                  </v-chip-group>
                </template>
              </v-data-table>

              <!-- Total Calculation for Filtered Results -->
              <v-card
                v-if="skuSearch && skuSearch.length > 0"
                class="mt-4"
                color="blue-grey lighten-5"
                elevation="2"
              >
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <v-icon left color="primary">mdi-calculator</v-icon>
                      <span class="text-h6 font-weight-bold">
                        Suchergebnisse Gesamt
                      </span>
                      <v-chip x-small class="ml-2" color="blue" dark>
                        {{ getFilteredSKUCount() }} SKUs
                      </v-chip>
                    </div>
                    <div class="text-right">
                      <div class="text-caption grey--text">Gesamtmenge</div>
                      <v-chip large color="primary" dark>
                        <v-icon left>mdi-package-variant</v-icon>
                        {{ getFilteredTotalQuantity() }}
                      </v-chip>
                    </div>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="text-caption grey--text">
                    <v-icon x-small>mdi-information</v-icon>
                    Zeigt Summe der Mengen für SKUs, die "{{ skuSearch }}" entsprechen
                  </div>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Orders per Shop Overview -->
      <v-row class="mt-4">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">
            <v-icon left>mdi-cart-outline</v-icon>
            Bestellungen pro Shop
          </h2>
        </v-col>

        <!-- Regular Shop Cards -->
        <v-col
          v-for="(shopData, shopName) in nonAcsShops"
          :key="shopName"
          cols="12"
          sm="6"
          md="3"
        >
          <v-card class="shop-order-card" elevation="3" :style="{ borderTop: `4px solid ${getShopColor(shopName)}` }">
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-avatar :color="getShopColor(shopName)" size="32" class="mr-2">
                  <v-icon dark small>{{ getShopIcon(shopName) }}</v-icon>
                </v-avatar>
                <div class="text-subtitle-1 font-weight-bold">{{ formatShopName(shopName) }}</div>
              </div>
              <div class="text-h4 primary--text mb-1">{{ shopData.orderCount }}</div>
              <div class="text-caption grey--text">
                <v-icon x-small>mdi-package-variant</v-icon>
                Bestellungen
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex justify-space-between text-caption">
                <span>
                  <v-icon x-small color="purple">mdi-cart-multiple</v-icon>
                  {{ shopData.multicartOrders || 0 }} Multi
                </span>
                <span>
                  <v-icon x-small color="blue">mdi-cart</v-icon>
                  {{ shopData.singlecartOrders || 0 }} Single
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ACS Customer Cards -->
        <template v-if="stats.byShop.acs">
          <v-col
            v-for="(customerData, customerName) in stats.byShop.acs.customers"
            :key="`acs-${customerName}`"
            cols="12"
            sm="6"
            md="3"
          >
            <v-card class="shop-order-card acs-card" elevation="3" :style="{ borderTop: `4px solid ${getShopColor('acs')}` }">
              <v-card-text>
                <div class="d-flex align-center mb-2">
                  <v-avatar :color="getShopColor('acs')" size="32" class="mr-2">
                    <v-icon dark small>{{ getShopIcon('acs') }}</v-icon>
                  </v-avatar>
                  <div class="text-subtitle-1 font-weight-bold">{{ customerName }}</div>
                </div>
                <div class="text-caption grey--text mb-1">
                  <v-chip x-small color="secondary" outlined>ACS</v-chip>
                </div>
                <div class="text-h4 primary--text mb-1">{{ customerData.orderCount }}</div>
                <div class="text-caption grey--text">
                  <v-icon x-small>mdi-package-variant</v-icon>
                  Bestellungen
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </template>
      </v-row>

      <!-- Multicart Not Ready Details -->
      <v-row class="mt-6" v-if="stats.multicartNotReadyOrders && stats.multicartNotReadyOrders.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">
            <v-icon left color="orange">mdi-cart-remove</v-icon>
            Multicart Nicht Bereit - Details
            <v-chip small color="orange" class="ml-2">{{ stats.multicartNotReady }}</v-chip>
          </h2>
        </v-col>

        <v-col cols="12">
          <v-card elevation="3">
            <v-card-text>
              <!-- Summary by Shop -->
              <div class="mb-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  <v-icon small left>mdi-chart-bar</v-icon>
                  Übersicht nach Shop
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <v-chip
                    v-for="(count, shopName) in stats.multicartNotReadyByShop"
                    :key="shopName"
                    :color="getShopColor(shopName)"
                    dark
                    class="mr-2 mb-2"
                  >
                    <v-icon small left>{{ getShopIcon(shopName) }}</v-icon>
                    {{ formatShopName(shopName) }}: {{ count }}
                  </v-chip>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- Filter Controls -->
              <div class="mb-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="selectedMulticartShopFilter"
                      :items="availableShopsInMulticartNotReady"
                      label="Filter nach Shop"
                      multiple
                      chips
                      clearable
                      deletable-chips
                      small-chips
                      outlined
                      dense
                      prepend-inner-icon="mdi-filter"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 3"
                          small
                          :color="getShopColor(item)"
                          dark
                          close
                          @click:close="removeMulticartShopFilter(item)"
                        >
                          <v-icon x-small left>{{ getShopIcon(item) }}</v-icon>
                          {{ formatShopName(item) }}
                        </v-chip>
                        <span
                          v-if="index === 3"
                          class="grey--text text-caption ml-2"
                        >
                          (+{{ selectedMulticartShopFilter.length - 3 }} weitere)
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-chip small :color="getShopColor(item)" dark class="mr-2">
                          <v-icon x-small left>{{ getShopIcon(item) }}</v-icon>
                          {{ formatShopName(item) }}
                        </v-chip>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-chip v-if="selectedMulticartShopFilter.length > 0" color="primary" outlined class="mr-2">
                      {{ filteredMulticartNotReadyOrders.length }} von {{ stats.multicartNotReadyOrders.length }} Bestellungen
                    </v-chip>
                    <v-btn
                      v-if="selectedMulticartShopFilter.length > 0"
                      text
                      small
                      color="primary"
                      @click="selectedMulticartShopFilter = []"
                    >
                      <v-icon small left>mdi-filter-remove</v-icon>
                      Filter zurücksetzen
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <!-- Detailed Orders Table -->
              <div class="text-subtitle-1 font-weight-bold mb-3">
                <v-icon small left>mdi-format-list-bulleted</v-icon>
                Bestellungen Details
              </div>

              <v-data-table
                :headers="multicartNotReadyHeaders"
                :items="filteredMulticartNotReadyOrders"
                :items-per-page="15"
                :sort-by.sync="multicartSortBy"
                :sort-desc.sync="multicartSortDesc"
                class="elevation-1"
                dense
              >
                <template v-slot:[`item.orderId`]="{ item }">
                  <v-chip small color="primary" outlined @click="openOrderInSupport(item)">
                    <v-icon x-small left>mdi-open-in-new</v-icon>
                    {{ item.orderId }}
                  </v-chip>
                </template>

                <template v-slot:[`item.shop`]="{ item }">
                  <v-chip x-small :color="getShopColor(item.shop)" dark>
                    <v-icon x-small left>{{ getShopIcon(item.shop) }}</v-icon>
                    {{ formatShopName(item.shop) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.acsCustomer`]="{ item }">
                  <span v-if="item.acsCustomer">{{ item.acsCustomer }}</span>
                  <span v-else class="grey--text">-</span>
                </template>

                <template v-slot:[`item.customer`]="{ item }">
                  <div class="text-caption">
                    {{ item.customer.firstName }} {{ item.customer.lastName }}
                    <v-chip x-small outlined class="ml-1">{{ item.customer.country }}</v-chip>
                  </div>
                </template>

                <template v-slot:[`item.progress`]="{ item }">
                  <div style="min-width: 120px;">
                    <v-progress-linear
                      :value="(item.totalConfirmed / item.totalExpected) * 100"
                      :color="getProgressColor(item.totalConfirmed, item.totalExpected)"
                      height="20"
                      rounded
                    >
                      <strong>{{ item.totalConfirmed }} / {{ item.totalExpected }}</strong>
                    </v-progress-linear>
                  </div>
                </template>

                <template v-slot:[`item.missingProducts`]="{ item }">
                  <v-menu offset-y max-width="800" min-width="700">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        x-small
                        :color="hasWarningProducts(item) ? 'red darken-2' : 'orange'"
                        :outlined="!hasWarningProducts(item)"
                        :dark="hasWarningProducts(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon x-small left>{{ hasWarningProducts(item) ? 'mdi-alert' : 'mdi-eye' }}</v-icon>
                        {{ Object.keys(item.missingProducts).length }} SKU(s)
                        <v-icon v-if="hasWarningProducts(item)" x-small right>mdi-alert</v-icon>
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title class="text-subtitle-1 font-weight-bold">
                        <v-icon left color="orange">mdi-alert-circle</v-icon>
                        Fehlende Produkte - Bestellung {{ item.orderId }}
                      </v-card-title>
                      <v-divider></v-divider>
                      <v-card-text class="pa-4">
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th>Status</th>
                                <th>SKU</th>
                                <th class="text-right">Erwartet</th>
                                <th class="text-right">Bestätigt</th>
                                <th class="text-right">Fehlt</th>
                                <th class="text-center">Reset</th>
                                <th class="text-center">Produziert / Heruntergeladen</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="(data, sku) in item.missingProducts"
                                :key="sku"
                                :class="{ 'warning-row': !data.inProduction && isOrderOlderThanDays(item, 2) }"
                              >
                                <td class="text-center">
                                  <v-tooltip bottom v-if="!data.inProduction && data.hasDamaged && isOrderOlderThanDays(item, 2)">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon color="red darken-2" small v-bind="attrs" v-on="on">
                                        mdi-alert
                                      </v-icon>
                                    </template>
                                    <span>WARNUNG: Item wurde zurückgesetzt und ist NICHT in Produktion! (Bestellung > 2 Tage alt)</span>
                                  </v-tooltip>
                                  <v-tooltip bottom v-else-if="!data.inProduction && isOrderOlderThanDays(item, 2)">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon color="orange darken-2" small v-bind="attrs" v-on="on">
                                        mdi-alert-circle-outline
                                      </v-icon>
                                    </template>
                                    <span>Item ist NICHT in Produktion (Bestellung > 2 Tage alt)</span>
                                  </v-tooltip>
                                  <v-icon v-else-if="data.inProduction" color="green" small>
                                    mdi-check-circle
                                  </v-icon>
                                  <v-icon v-else color="grey lighten-1" x-small>
                                    mdi-circle-small
                                  </v-icon>
                                </td>
                                <td class="font-weight-medium">{{ sku }}</td>
                                <td class="text-right">{{ data.expected }}</td>
                                <td class="text-right">
                                  <span :class="data.confirmed > 0 ? 'green--text' : 'grey--text'">
                                    {{ data.confirmed }}
                                  </span>
                                </td>
                                <td class="text-right">
                                  <v-chip x-small color="red" dark>{{ data.missing }}</v-chip>
                                </td>
                                <td class="text-center">
                                  <v-tooltip bottom v-if="data.hasDamaged !== undefined">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon
                                        :color="data.hasDamaged ? 'orange darken-2' : 'grey lighten-1'"
                                        small
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        {{ data.hasDamaged ? 'mdi-refresh-circle' : 'mdi-check-circle-outline' }}
                                      </v-icon>
                                    </template>
                                    <span>{{ data.hasDamaged ? 'Item wurde zurückgesetzt (damaged)' : 'Kein Reset' }}</span>
                                  </v-tooltip>
                                  <span v-else class="grey--text text-caption">-</span>
                                </td>
                                <td class="text-center">
                                  <v-tooltip bottom v-if="data.inProduction !== undefined">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-chip
                                        x-small
                                        :color="data.inProduction ? 'green' : 'red'"
                                        dark
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        <v-icon x-small left>
                                          {{ data.inProduction ? 'mdi-check' : 'mdi-close' }}
                                        </v-icon>
                                        {{ data.inProduction ? 'Ja' : 'Nein' }}
                                      </v-chip>
                                    </template>
                                    <span>{{ data.inProduction ? 'Neuer offener Item in Produktion' : 'Kein offener Item verfügbar' }}</span>
                                  </v-tooltip>
                                  <span v-else class="grey--text text-caption">-</span>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </template>

                <template v-slot:[`item.createDate`]="{ item }">
                  <div class="text-caption">{{ formatTimestamp(item.createDate) }}</div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Shop Breakdown -->
      <v-row class="mt-4">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">
            <v-icon left>mdi-store-outline</v-icon>
            Detaillierte Shop-Aufschlüsselung
          </h2>
        </v-col>

        <v-col
          v-for="(shopData, shopName) in stats.byShop"
          :key="shopName"
          cols="12"
          lg="6"
        >
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header class="shop-header">
                <div class="d-flex align-center">
                  <v-chip :color="getShopColor(shopName)" dark>
                    <v-icon left>{{ getShopIcon(shopName) }}</v-icon>
                    {{ formatShopName(shopName) }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <v-chip outlined class="mr-2">
                    <v-icon small left>mdi-package-variant-closed</v-icon>
                    {{ getTotalSKUCountForShop(shopData) }} Items
                  </v-chip>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-card flat class="shop-card"  elevation="0">
              <!-- SKU Table for Shop -->
              <div class="sku-section mb-4">
                <h3 class="text-subtitle-1 mb-2">
                  <v-icon small left>mdi-format-list-bulleted</v-icon>
                  SKU-Aufschlüsselung
                </h3>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">SKU</th>
                        <th class="text-right">Menge</th>
                        <th class="text-right">% des Shops</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(count, sku) in shopData.skuCounts"
                        :key="sku"
                      >
                        <td class="font-weight-medium">{{ sku }}</td>
                        <td class="text-right">
                          <v-chip small color="primary" outlined>{{ count }}</v-chip>
                        </td>
                        <td class="text-right">
                          {{ getPercentage(count, getTotalSKUCountForShop(shopData)) }}%
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>

              <!-- ACS Customer Breakdown -->
              <div
                v-if="shopName === 'acs' && Object.keys(shopData.customers).length > 0"
                class="customers-section"
              >
                <v-divider class="my-4"></v-divider>
                <h3 class="text-subtitle-1 mb-3">
                  <v-icon small left>mdi-account-group</v-icon>
                  Kunden-Aufschlüsselung
                </h3>

                <v-expansion-panels accordion>
                  <v-expansion-panel
                    v-for="(customerData, customerName) in shopData.customers"
                    :key="customerName"
                  >
                    <v-expansion-panel-header>
                      <div class="d-flex align-center justify-space-between">
                        <span class="font-weight-bold">{{ customerName }}</span>
                        <v-chip small color="secondary" outlined>
                          {{ getTotalSKUCountForCustomer(customerData) }} Items
                        </v-chip>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">SKU</th>
                              <th class="text-right">Menge</th>
                              <th class="text-right">% des Kunden</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(count, sku) in customerData.skuCounts"
                              :key="sku"
                            >
                              <td class="font-weight-medium">{{ sku }}</td>
                              <td class="text-right">
                                <v-chip x-small color="secondary" outlined>{{ count }}</v-chip>
                              </td>
                              <td class="text-right">
                                {{ getPercentage(count, getTotalSKUCountForCustomer(customerData)) }}%
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Refresh Button -->
      <v-row class="mt-4">
        <v-col cols="12" class="text-center">
          <v-btn
            @click="fetchData"
            color="primary"
            large
            :loading="loading"
          >
            <v-icon left>mdi-refresh</v-icon>
            Daten aktualisieren
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import Api from '@/services/Api';

export default {
  name: 'EnhancedAdminDashboard',
  data() {
    return {
      stats: null,
      loading: true,
      error: null,
      autoRefreshInterval: null,
      skuSearch: '',
      selectedShopFilter: [],
      selectedMulticartShopFilter: [],
      multicartSortBy: 'createDate',
      multicartSortDesc: true,
      skuTableHeaders: [
        { text: 'SKU', value: 'sku', sortable: true },
        { text: 'Menge', value: 'quantity', sortable: true, align: 'right' },
        { text: '% der Gesamt', value: 'percentage', sortable: true, align: 'right' },
        { text: 'Shops', value: 'shops', sortable: false }
      ],
      multicartNotReadyHeaders: [
        { text: 'Bestell-ID', value: 'orderId', sortable: true },
        { text: 'Shop', value: 'shop', sortable: true },
        { text: 'ACS Kunde', value: 'acsCustomer', sortable: true },
        { text: 'Kunde', value: 'customer', sortable: false },
        { text: 'Fortschritt', value: 'progress', sortable: false, align: 'center' },
        { text: 'Fehlende Produkte', value: 'missingProducts', sortable: false, align: 'center' },
        { text: 'Erstellt', value: 'createDate', sortable: true }
      ],
      oldOrdersHeaders: [
        { text: 'Bestell-ID', value: 'orderId', sortable: true },
        { text: 'Shop', value: 'shop', sortable: true },
        { text: 'ACS Kunde', value: 'acsCustomer', sortable: true },
        { text: 'Alter', value: 'daysOld', sortable: true, align: 'center' },
        { text: 'Prime', value: 'isPrime', sortable: true, align: 'center' },
        { text: 'Kunde', value: 'customer', sortable: false },
        { text: 'Referenz', value: 'reference', sortable: false },
        { text: 'Erstellt', value: 'createDate', sortable: true }
      ],
      // SKUs to ignore/filter out from the display
      ignoredSkus: [
        "Artikel_Extras",
        "LYTO_TYPE_C_KABEL",
        "LYTO_NETZSTECKER",
        "LYTO_LED_SOCKEL_WHITE",
        "wooden_led_base_HV",
        "wooden_led_base_RGB",
        "NETZSTECKER",
        "TYP_C_USB_KABEL",
        "gravurfarbe_kinderbesteck",
        "PRIO_ORDER",
        "LYTO_SAMTBEUTEL_SCHWARZ",
        "LS24_SAMTBEUTEL_RED",
        "LS24_SAMTBEUTEL_SILVER",
        "LS24_SAMTBEUTEL_BLACK",
        "Samtbeutel (schwarz)",
        "Samtbeutel (rot)",
        "GF_Organzasäckchen",
        "GF_Samtsäckchen",
        "hauck_holzleiste_5mm",
        "upsell_holzsockel5mm",
        "samtbeutel_kordelzug",
        "stoffbeutel",
        "shopify_extra_helle_gravur",
        "Taschenmesserkette",
        "fruehstuecksbrett_tiere_upsell"
      ]
    };
  },
  computed: {
    filteredSKUData() {
      return this.getOverallSKUData();
    },

    availableShopsInOldOrders() {
      if (!this.stats || !this.stats.oldOrders) return [];
      const shops = [...new Set(this.stats.oldOrders.map(order => order.shop))];
      return shops.sort();
    },

    filteredOldOrders() {
      if (!this.stats || !this.stats.oldOrders) return [];
      if (this.selectedShopFilter.length === 0) {
        return this.stats.oldOrders;
      }
      return this.stats.oldOrders.filter(order =>
        this.selectedShopFilter.includes(order.shop)
      );
    },

    nonAcsShops() {
      if (!this.stats || !this.stats.byShop) return {};
      const shops = {};
      Object.entries(this.stats.byShop).forEach(([shopName, shopData]) => {
        if (shopName !== 'acs') {
          shops[shopName] = shopData;
        }
      });
      return shops;
    },

    availableShopsInMulticartNotReady() {
      if (!this.stats || !this.stats.multicartNotReadyOrders) return [];
      const shops = [...new Set(this.stats.multicartNotReadyOrders.map(order => order.shop))];
      return shops.sort();
    },

    filteredMulticartNotReadyOrders() {
      if (!this.stats || !this.stats.multicartNotReadyOrders) return [];
      if (this.selectedMulticartShopFilter.length === 0) {
        return this.stats.multicartNotReadyOrders;
      }
      return this.stats.multicartNotReadyOrders.filter(order =>
        this.selectedMulticartShopFilter.includes(order.shop)
      );
    }
  },
  mounted() {
    this.fetchData();
    // Auto-refresh every 2 minutes
    this.autoRefreshInterval = setInterval(() => {
      this.fetchData(true);
    }, 120000);
  },
  beforeDestroy() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  },
  methods: {
    async fetchData(silent = false) {
      if (!silent) {
        this.loading = true;
      }
      this.error = null;

      try {
        const response = await Api().get('dashboard/enhanced');
        this.stats = response.data;
      } catch (err) {
        console.error('Error fetching enhanced dashboard data:', err);
        this.error = err.response?.data?.error || 'Failed to load dashboard data';
      } finally {
        this.loading = false;
      }
    },

    formatShopName(shop) {
      const names = {
        amazon: 'Amazon',
        shopify: 'Shopify',
        acs: 'ACS',
        muckelmeister: 'Muckelmeister',
        etsy: 'Etsy'
      };
      return names[shop] || shop.charAt(0).toUpperCase() + shop.slice(1);
    },

    getShopColor(shop) {
      const colors = {
        amazon: 'orange darken-2',
        shopify: 'green darken-1',
        acs: 'blue darken-1',
        muckelmeister: 'purple darken-1',
        etsy: 'deep-orange darken-1'
      };
      return colors[shop] || 'grey';
    },

    getShopIcon(shop) {
      const icons = {
        amazon: 'mdi-amazon',
        shopify: 'mdi-shopify',
        acs: 'mdi-office-building',
        muckelmeister: 'mdi-teddy-bear',
        etsy: 'mdi-shopping'
      };
      return icons[shop] || 'mdi-store';
    },

    getTotalUniqueSKUs() {
      if (!this.stats) return 0;
      const allSKUs = new Set();
      Object.values(this.stats.byShop).forEach(shop => {
        Object.keys(shop.skuCounts).forEach(sku => allSKUs.add(sku));
      });
      return allSKUs.size;
    },

    getTotalSKUCountForShop(shopData) {
      return Object.values(shopData.skuCounts).reduce((sum, count) => sum + count, 0);
    },

    getTotalSKUCountForCustomer(customerData) {
      return Object.values(customerData.skuCounts).reduce((sum, count) => sum + count, 0);
    },

    getPercentage(count, total) {
      if (total === 0) return 0;
      return ((count / total) * 100).toFixed(1);
    },

    getProgressColor(confirmed, expected) {
      const percentage = (confirmed / expected) * 100;
      if (percentage === 0) return 'red';
      if (percentage < 50) return 'orange';
      if (percentage < 100) return 'amber';
      return 'green';
    },

    getProductionPercentage(count) {
      if (!this.stats || !this.stats.totalOrders) return 0;
      return ((count / this.stats.totalOrders) * 100).toFixed(1);
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return 'N/A';
      return new Date(timestamp).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getOverallSKUData() {
      if (!this.stats) return [];

      // Aggregate all SKUs across all shops
      const skuMap = new Map();
      let totalQuantity = 0;

      Object.entries(this.stats.byShop).forEach(([shopName, shopData]) => {
        Object.entries(shopData.skuCounts).forEach(([sku, count]) => {
          // Skip ignored SKUs
          if (this.ignoredSkus.includes(sku)) {
            return;
          }

          totalQuantity += count;

          if (!skuMap.has(sku)) {
            skuMap.set(sku, {
              sku: sku,
              quantity: 0,
              shops: []
            });
          }

          const skuData = skuMap.get(sku);
          skuData.quantity += count;
          if (!skuData.shops.includes(shopName)) {
            skuData.shops.push(shopName);
          }
        });
      });

      // Calculate percentages and convert to array
      const skuArray = Array.from(skuMap.values()).map(item => ({
        ...item,
        percentage: totalQuantity > 0 ? (item.quantity / totalQuantity) * 100 : 0
      }));

      // Sort by quantity (descending)
      return skuArray.sort((a, b) => b.quantity - a.quantity);
    },

    getProgressColor(percentage) {
      if (percentage >= 10) return 'red';
      if (percentage >= 5) return 'orange';
      if (percentage >= 2) return 'yellow darken-2';
      return 'green';
    },

    getFilteredSKUCount() {
      if (!this.skuSearch || !this.stats) return 0;

      const allSkus = this.getOverallSKUData();
      const searchLower = this.skuSearch.toLowerCase();

      return allSkus.filter(item =>
        item.sku.toLowerCase().includes(searchLower)
      ).length;
    },

    getFilteredTotalQuantity() {
      if (!this.skuSearch || !this.stats) return 0;

      const allSkus = this.getOverallSKUData();
      const searchLower = this.skuSearch.toLowerCase();

      return allSkus
        .filter(item => item.sku.toLowerCase().includes(searchLower))
        .reduce((sum, item) => sum + item.quantity, 0);
    },

    getOrderTypePercentage(count) {
      if (!this.stats || !this.stats.totalOrders || this.stats.totalOrders === 0) return 0;
      return ((count / this.stats.totalOrders) * 100).toFixed(1);
    },

    getMulticartReadyPercentage() {
      if (!this.stats || !this.stats.multicartOrders || this.stats.multicartOrders === 0) return 0;
      const ready = this.stats.multicartReady || 0;
      return ((ready / this.stats.multicartOrders) * 100).toFixed(1);
    },

    getMulticartNotReadyPercentage() {
      if (!this.stats || !this.stats.multicartOrders || this.stats.multicartOrders === 0) return 0;
      const notReady = this.stats.multicartNotReady || 0;
      return ((notReady / this.stats.multicartOrders) * 100).toFixed(1);
    },

    getProductionPercentage(count) {
      if (!this.stats || !this.stats.totalOrders || this.stats.totalOrders === 0) return 0;
      return ((count / this.stats.totalOrders) * 100).toFixed(1);
    },

    getPrimePercentage() {
      if (!this.stats || !this.stats.totalOrders || this.stats.totalOrders === 0) return 0;
      const primeOrders = this.stats.primeOrders || 0;
      return ((primeOrders / this.stats.totalOrders) * 100).toFixed(1);
    },

    getDaysOldColor(days) {
      if (days >= 14) return 'red darken-2';
      if (days >= 10) return 'deep-orange';
      if (days >= 7) return 'orange';
      return 'grey';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },

    removeShopFilter(shop) {
      const index = this.selectedShopFilter.indexOf(shop);
      if (index >= 0) {
        this.selectedShopFilter.splice(index, 1);
      }
    },

    removeMulticartShopFilter(shop) {
      const index = this.selectedMulticartShopFilter.indexOf(shop);
      if (index >= 0) {
        this.selectedMulticartShopFilter.splice(index, 1);
      }
    },

    isOrderOlderThanDays(order, days) {
      if (!order.createDate) return false;

      const orderDate = new Date(order.createDate);
      const now = new Date();
      const diffTime = now - orderDate;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      return diffDays > days;
    },

    hasWarningProducts(order) {
      // Check if any missing product is not in production AND order is older than 2 days
      if (!order.missingProducts) return false;
      if (!this.isOrderOlderThanDays(order, 2)) return false;

      return Object.values(order.missingProducts).some(product =>
        product.inProduction === false
      );
    },

    openOrderInSupport(order) {
      const url = `https://backoffice.accelery.host/support?search=${order.orderId}`;
      window.open(url, '_blank');
    }
  }
};
</script>

<style scoped>
.enhanced-dashboard {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.summary-card {
  height: 100%;
  transition: transform 0.2s;
}

.summary-card:hover {
  transform: translateY(-4px);
}

.shop-card {
  margin-bottom: 16px;
}

.shop-header {
  background-color: #fafafa;
}

.sku-section {
  max-height: 400px;
  overflow-y: auto;
}

.sku-overview-table {
  background-color: white;
}

.sku-overview-table >>> .v-data-table__wrapper {
  max-height: 600px;
  overflow-y: auto;
}

.customers-section {
  margin-top: 16px;
}

.v-data-table {
  background-color: transparent !important;
}

.v-expansion-panel {
  margin-bottom: 8px;
}

.compact-card {
  transition: transform 0.2s;
}

.compact-card:hover {
  transform: translateY(-2px);
}

.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.clickable-rows >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.shop-order-card {
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
}

.shop-order-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}

.acs-card {
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
}

.warning-row {
  background-color: rgba(255, 152, 0, 0.1) !important;
  border-left: 4px solid #f57c00 !important;
}

.warning-row:hover {
  background-color: rgba(255, 152, 0, 0.15) !important;
}
</style>
